import { Button, Skeleton } from '@up2stay/up2promo-ui';
import { UserPlus } from 'lucide-react';

import { PlanLockBadge } from '@/core/components/billing/upgrade-surfaces';
import { ShellLink } from '@/core/components/navigation/shell-link';
import { canPurchaseBillingInApp } from '@/core/billing/can-purchase-billing-in-app';
import { billingUpgradePath } from '@/core/billing/upgrade-focus';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useDictionary } from '@/core/providers/dictionary-provider';

interface InviteMemberHeaderActionProps {
  onInvite: () => void;
}

export function InviteMemberHeaderAction({
  onInvite,
}: InviteMemberHeaderActionProps) {
  const dict = useDictionary();
  const label = dict.workspace.users.inviteUser;
  const hint = dict.entitlements.upgrade.inviteMembersHint;
  const { isFeatureEnabled, isLoading } = useEntitlements();

  if (isLoading) {
    return (
      <Skeleton className="h-10 min-h-10 w-32 shrink-0 rounded-[var(--r-control)]" />
    );
  }

  if (!isFeatureEnabled('feature.invite_members')) {
    if (!canPurchaseBillingInApp()) {
      return (
        <Button
          variant="outline"
          size="md"
          className="h-10 min-h-10 shrink-0 px-3 sm:px-4"
          disabled
          data-guidance-target="people-invite"
          aria-label={`${label}. ${hint}`}
        >
          <UserPlus className="mr-2 h-5 w-5" />
          <span>{label}</span>
          <PlanLockBadge feature="feature.invite_members" className="ml-2" />
        </Button>
      );
    }
    return (
      <Button
        variant="outline"
        size="md"
        className="h-10 min-h-10 shrink-0 px-3 sm:px-4"
        asChild
      >
        <ShellLink
          to={billingUpgradePath('feature.invite_members')}
          data-guidance-target="people-invite"
          aria-label={`${label}. ${hint}`}
        >
          <UserPlus className="mr-2 h-5 w-5" />
          <span>{label}</span>
          <PlanLockBadge feature="feature.invite_members" className="ml-2" />
        </ShellLink>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      size="md"
      className="h-10 min-h-10 shrink-0 px-3 sm:px-4"
      data-guidance-target="people-invite"
      aria-label={label}
      onClick={onInvite}
    >
      <UserPlus className="mr-2 h-5 w-5" />
      <span>{label}</span>
    </Button>
  );
}
